import { Spinner } from './feedback';
import { addonKindsOf, addonLoaderOf, addonLabelOf } from '@mcpanel/core';
import { api } from '../api/client';
// Mod・プラグインの検索（Modrinth / CurseForge）。サーバーの Mod タブとテンプレートの編集で使う。
import { useEffect, useId, useState } from 'react';
import { api3 } from '../api/client-p3';
import type { AddonSourceName, ModSearchHit } from '../api/types-p3';
import { errorMessage } from '../app-context';
import { Empty } from './ui';

export const sourceLabel: Record<AddonSourceName, string> = { modrinth: 'Modrinth', curseforge: 'CurseForge' };
export const isPluginFamily = (family: string) => addonKindsOf(family).plugins && !addonKindsOf(family).mods;
export const addonWord = addonLabelOf;

export function sideLabel(entry: { env: { client: string; server: string } }): string {
  if (entry.env.client === 'unknown' && entry.env.server === 'unknown') return '';
  if (entry.env.client === 'unsupported') return 'サーバーのみ';
  if (entry.env.server === 'unsupported') return 'クライアントのみ';
  if (entry.env.client === 'optional') return '両方（クライアントは任意）';
  return '両方に必要';
}

export function AddonIcon({ url, title, size = 32 }: { url?: string; title: string; size?: number }) {
  return url ? (
    <img src={url} alt="" width={size} height={size} style={{ borderRadius: 6, flexShrink: 0 }} />
  ) : (
    <span
      aria-hidden
      style={{
        width: size,
        height: size,
        borderRadius: 6,
        background: 'var(--surface-2)',
        display: 'grid',
        placeItems: 'center',
        flexShrink: 0,
      }}
    >
      {title.slice(0, 1)}
    </span>
  );
}

export function SourceTag({ source }: { source: AddonSourceName }) {
  return source === 'modrinth' ? null : <span className="tag">{sourceLabel[source]}</span>;
}

/** 検索結果の 1 件に付ける状態（導入済み・追加予定なら追加ボタンを止める） */
export type HitState = 'installed' | 'staged' | undefined;

export function AddonSearch({
  family,
  kind: selectedKind,
  mcVersion,
  sources,
  stateOf,
  onAdd,
  title,
  target = 'server',
  initialQuery = '',
  modpack = false,
}: {
  family: string;
  kind?: 'mod' | 'plugin';
  mcVersion: string;
  /** 使える取得元。省略時は API に問い合わせる */
  sources?: AddonSourceName[];
  stateOf: (hit: ModSearchHit) => HitState;
  onAdd: (hit: ModSearchHit, kind: 'mod' | 'plugin') => void;
  title?: string;
  target?: 'server' | 'client';
  initialQuery?: string;
  modpack?: boolean;
}) {
  const [choice, setChoice] = useState<'mod' | 'plugin'>(isPluginFamily(family) ? 'plugin' : 'mod');
  const kinds = addonKindsOf(family);
  const kind = selectedKind ?? (kinds.mods && kinds.plugins ? choice : kinds.plugins ? 'plugin' : 'mod');
  const word = modpack ? 'Modpack' : kind === 'plugin' ? 'プラグイン' : 'Mod';
  const queryId = useId();
  const [available, setAvailable] = useState<AddonSourceName[]>(sources ?? ['modrinth']);
  const [source, setSource] = useState<AddonSourceName>('modrinth');
  const [query, setQuery] = useState(initialQuery);
  useEffect(() => setQuery(initialQuery), [initialQuery]);
  const [hits, setHits] = useState<ModSearchHit[]>();
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (sources) {
      setAvailable(sources);
      return;
    }
    let active = true;
    api3
      .modSources()
      .then((result) => active && setAvailable(result.sources))
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [sources]);

  // 条件が変わったら古い結果は消す
  useEffect(() => setHits(undefined), [family, kind, mcVersion, source]);

  const search = async () => {
    if (!mcVersion && !modpack) {
      setError('先に Minecraft のバージョンを選んでください');
      return;
    }
    setBusy(true);
    setError(undefined);
    try {
      setHits(
        modpack
          ? await api.searchModpacks(query, source)
          : await api3.searchMods({
              q: query,
              loader: target === 'client' ? family : addonLoaderOf(family, kind)!,
              kind,
              mcVersion,
              source,
            }),
      );
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card">
      <div className="card-header">
        <h2>{title ?? `${word}を追加`}</h2>
        <span className="small faint">
          {modpack
            ? 'Modrinth / CurseForge の Modpack'
            : family === 'velocity'
              ? 'Velocity に対応するプラグインを表示'
              : `${family} ${mcVersion} に対応するものだけ表示`}
        </span>
      </div>
      <div className="card-body stack" style={{ gap: 10 }}>
        {/* テンプレート編集の form の中でも使うので、form にはしない（Enter は入力欄で拾う） */}
        <div className="row">
          {!modpack && !selectedKind && kinds.mods && kinds.plugins && (
            <label>
              種類
              <select className="input" value={kind} onChange={(e) => setChoice(e.target.value as 'mod' | 'plugin')}>
                <option value="mod">Mod</option>
                <option value="plugin">プラグイン</option>
              </select>
            </label>
          )}
          {available.length > 1 && (
            <div className="row" role="group" aria-label="検索先" style={{ gap: 4 }}>
              {available.map((name) => (
                <button
                  key={name}
                  type="button"
                  className={`btn small${source === name ? ' primary' : ''}`}
                  aria-pressed={source === name}
                  onClick={() => setSource(name)}
                >
                  {sourceLabel[name]}
                </button>
              ))}
            </div>
          )}
          <div className="field" style={{ flex: 1, minWidth: 160, margin: 0 }}>
            <label htmlFor={queryId}>{word}を検索</label>
            <input
              id={queryId}
              className="input"
              style={{ flex: 1, minWidth: 160 }}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key !== 'Enter' || e.nativeEvent.isComposing) return;
                e.preventDefault();
                void search();
              }}
              placeholder={
                modpack
                  ? 'Modpack 名（例: Cobblemon）'
                  : kind === 'plugin'
                    ? 'プラグイン名（例: LuckPerms）'
                    : 'Mod 名（例: lithium）'
              }
              aria-label={`${word}を検索`}
              aria-describedby={error ? `${queryId}-error` : undefined}
            />
            {error && (
              <div id={`${queryId}-error`} role="alert" className="small" style={{ color: 'var(--danger)' }}>
                {error}
              </div>
            )}
          </div>
          <button type="button" className="btn" disabled={busy} onClick={() => void search()}>
            {busy && <Spinner />}
            検索
          </button>
        </div>
        {source === 'curseforge' && (
          <div className="small faint">
            CurseForge
            の一部のファイルは作者が外部からの自動ダウンロードを許可していないため、追加できないことがあります。
          </div>
        )}
        {hits && hits.length === 0 && <Empty>見つかりませんでした。</Empty>}
        {hits?.map((hit) => {
          const state = stateOf(hit);
          // サーバーの Mod タブでは、クライアント専用の Mod も「配布物にだけ」入れられる（プラグインは不可）
          const clientOnly = !modpack && target === 'server' && kind === 'mod' && hit.serverSide === 'unsupported';
          const unsupported =
            !modpack &&
            !clientOnly &&
            (target === 'client' ? hit.clientSide === 'unsupported' : hit.serverSide === 'unsupported');
          return (
            <div
              key={`${hit.source}:${hit.projectId}`}
              className="row feedback-item"
              style={{ alignItems: 'flex-start', gap: 10 }}
            >
              <AddonIcon url={hit.iconUrl} title={hit.title} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <strong>{hit.title}</strong>{' '}
                <span className="small faint">{hit.downloads.toLocaleString('ja-JP')} DL</span>
                <div className="small muted">{hit.description}</div>
                {modpack && (
                  <div className="small faint">
                    {hit.categories
                      .filter((category) => ['fabric', 'forge', 'neoforge', 'quilt'].includes(category))
                      .join(', ')}{' '}
                    / Minecraft {hit.versions?.join(', ')}
                  </div>
                )}
                <div className="small faint">
                  {sideLabel({ env: { client: hit.clientSide, server: hit.serverSide } })}
                </div>
              </div>
              <button
                type="button"
                className="btn small"
                disabled={state !== undefined || unsupported}
                onClick={() => onAdd(hit, kind)}
                title={
                  unsupported
                    ? target === 'client'
                      ? 'クライアントで使えない Mod です'
                      : `クライアント専用の${word}はサーバーに入れません`
                    : clientOnly
                      ? 'サーバーには置かず、ランチャーの配布物にだけ入れます'
                      : undefined
                }
              >
                {state === 'installed'
                  ? '導入済み'
                  : state === 'staged'
                    ? '追加予定'
                    : clientOnly
                      ? '配布物にだけ追加'
                      : '追加'}
              </button>
            </div>
          );
        })}
      </div>
    </section>
  );
}
